import React, { Fragment } from 'react';
import { connect, DispatchProp } from 'react-redux';
import core, { App, GQL, View, Viewport } from '@gin-betting/core';
import cn from 'classnames';
import cd from 'debug';
import { difference, noop } from 'lodash';
import { compose } from 'redux';
import { createSelector } from 'reselect';
import { Arg1 } from 'tsargs';

import { LIVE_SPORTEVENT_STATUSES, SELECTOR_STUB } from 'app-constants';
import {
    MarketsCount,
    MatchDateTime,
    MiddlePartMatchRow,
    SmartLink,
    SportEventInViewSubscription,
    SportEventSeoMicromarking,
    StreamLink,
    TeamsTitle,
    TournamentName,
} from 'components';
import {
    withStreamModule,
    WithStreamModuleTypes,
    withStreamWidgetSettings,
    WithStreamWidgetSettingsTypes,
} from 'hoc';
import { AsyncOddButton } from 'modules/betslip/async';
import favorite from 'modules/favorite';
import { AsyncStream } from 'modules/stream/async';
import {
    selectBetradarId,
    selectIsSportEventViewable,
    selectRouteBySportEvent,
} from 'selectors';
import { Action, AnalyticAttrsCtx, Category } from 'services/analytics';
import type { ParentPath, State as AppState } from 'types';
import {
    checkIfNewsPage,
    checkIsMatchTrackerDisabled,
    checkIsSportEventViewable,
    topMarketsVerify,
} from 'utils';
import { getTopMarketComponentsBySport } from '../../getTopMarkets';
import { selectTopMarketsByMatchId } from '../../selectors';
import { TopMarketsContainer } from './Markets';
import Score from './Score';
import StreamMarketCount from './StreamMarketCount';

import './sportEventRow.scss';

const {
    hocs: { makePure },
    entities: {
        selectors: {
            selectMatchCompetitorsValid,
            makeMatchSelector,
            makeTournamentSelector,
            selectPreviewTournamentIdFromMatch,
            selectMobileMatchStream,
        },
    },
    viewport: {
        selectors: { makeViewportSelector },
    },
} = core;
const { ToggleFavoriteButton } = favorite.components;
const { isHCP, isTotal } = topMarketsVerify;

const debug = cd('match-list:components:MatchRowContainer');

const matchPropsToPick = [
    'title' as const,
    'id' as const,
    'marketsCount' as const,
    'sportId' as const,
    'streams' as const,
    'liveCoverage' as const,
    'status' as const,
    'startTime' as const,
    'tournament' as const,
    'score' as const,
    'slug' as const,
    'hasMatchLog' as const,
    'version' as const,
];

const tournamentPropsToPick = [
    'id' as const,
    'name' as const,
    'masterId' as const,
    'slug' as const,
];

type Match = Pick<View.Match, typeof matchPropsToPick[number]>;

type Tournament = Pick<View.Tournament, typeof tournamentPropsToPick[number]>;

interface MappedProps {
    match: Match | null;
    tournament: Tournament | null;
    competitorsValid: View.CompetitorsValid;
    lessThanOrEqual: Viewport.Breakpoints;
    hasHeader: boolean;
    topMarketsCount: number;
    streamUrl: ReturnType<typeof selectMobileMatchStream>;
    markets: ReturnType<typeof selectTopMarketsByMatchId>;
    isStreamOpen: boolean | null;
    isOneMarketView: boolean;
    count: number;
    scoreCount: number;
    hasValidStream: boolean;
    hasValidBetradarId: boolean;
    betradarId: string | null;
    route: ParentPath;
}

interface MappedActions {
    addMatchId:
        | WithStreamModuleTypes.StreamModule['actions']['addMatchId']
        | VoidFn;
    deleteMatchId:
        | WithStreamModuleTypes.StreamModule['actions']['deleteMatchId']
        | VoidFn;
}

interface OwnProps {
    matchId: string;
    prevMatchId: string | undefined;
    isResultPage?: boolean;
}

interface ConnectOwnProps
    extends OwnProps,
        WithStreamModuleTypes.WithStreamModule {}

type Props = OwnProps &
    MappedProps &
    MappedActions &
    WithStreamModuleTypes.WithStreamModule &
    WithStreamWidgetSettingsTypes;
//  & ResponsiveComponents.ResponseContainerProps;

type State = {
    isOneMarketView: boolean;
};

const ANALYTICS = {
    'data-category': Category.LineSportEvent,
    'data-action': Action.SelectOdd,
};

const ANALYTICS_CLICK_ON_TITLE = {
    'data-action': Action.ClickOnTitle,
    'data-category': Category.LineSportEvent,
};

const ANALYTICS_PROMO = {
    category: Category.LineSportEvent,
};

const ANALYTICS_ODD_BTN_STREAM = {
    'data-category': Category.LineSportEvent,
    'data-action': Action.SelectOdd,
};

const getStreamSettings = ({
    hasValidStream,
    hasValidBetradarId,
    isMatchTrackerDisabled,
    isSmall,
    isStreamOpen,
}: {
    hasValidStream: boolean;
    hasValidBetradarId: boolean;
    isMatchTrackerDisabled: boolean;
    isSmall: boolean;
    isStreamOpen: boolean | null;
}) => {
    const isStreamAvailable =
        (hasValidStream || (hasValidBetradarId && !isMatchTrackerDisabled)) &&
        !isSmall;

    const isViewerOpen = !!isStreamOpen && isStreamAvailable;

    return {
        isViewerOpen,
        isStreamAvailable,
    };
};

class SportEventRow extends React.Component<Props, State> {
    state: State = {
        isOneMarketView: true,
    };

    constructor(props: Props) {
        super(props);
        const { isOneMarketView } = props;

        this.state.isOneMarketView = isOneMarketView;
    }

    componentDidUpdate() {
        const { isOneMarketView } = this.state;
        const { isOneMarketView: isOneMarketViewProps } = this.props;

        if (isOneMarketView && !isOneMarketViewProps) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                isOneMarketView: false,
            });
        }
    }

    getLinkToMatch = (): string | null => {
        const { isResultPage, match, route } = this.props;

        return match
            ? `${route}${
                  isResultPage ? App.Routes.RESULT_MATCH : App.Routes.MATCH
              }${match.slug}`
            : null;
    };

    toggleStream = (): void => {
        const {
            matchId,
            hasValidStream,
            hasValidBetradarId,
            isStreamOpen,
            lessThanOrEqual,
            disableMatchTracker,
            match,
        } = this.props;

        const isSmall = lessThanOrEqual.small;

        const { isViewerOpen } = getStreamSettings({
            hasValidStream,
            hasValidBetradarId,
            isMatchTrackerDisabled: checkIsMatchTrackerDisabled(
                disableMatchTracker,
                match?.sportId
            ),
            isSmall,
            isStreamOpen,
        });

        if (isViewerOpen) {
            this.props.deleteMatchId({ matchId });
        } else {
            this.props.addMatchId({ matchId, withClearPrev: true });
        }
    };

    renderMiddle = (): JSX.Element | null => {
        const { isOneMarketView } = this.state;
        const {
            match,
            hasHeader,
            competitorsValid,
            count,
            scoreCount,
        } = this.props;

        if (!match) return null;

        const { id: matchId, sportId, status } = match;
        const isEnded =
            status === GQL.SportEventStatus.ENDED ||
            status === GQL.SportEventStatus.CLOSED;

        const showScores =
            [...LIVE_SPORTEVENT_STATUSES, GQL.SportEventStatus.ENDED].includes(
                status
            ) && !checkIfNewsPage();

        const { awayLogo, awayName, homeLogo, homeName } = competitorsValid;
        const linkToMatch = this.getLinkToMatch();

        if (isOneMarketView) {
            return (
                <MiddlePartMatchRow
                    sportId={sportId}
                    linkToMatch={linkToMatch}
                    homeTitle={homeName}
                    awayTitle={awayName}
                    homeLogo={homeLogo}
                    awayLogo={awayLogo}
                >
                    <AnalyticAttrsCtx.Provider value={ANALYTICS}>
                        <TopMarketsContainer
                            matchId={matchId}
                            hasMatchHeader={hasHeader}
                            sportId={sportId}
                            count={1}
                        />
                    </AnalyticAttrsCtx.Provider>
                </MiddlePartMatchRow>
            );
        }

        return (
            <Fragment>
                <div styleName="teamsTitle">
                    <TeamsTitle
                        sportId={sportId}
                        competitorsValid={competitorsValid}
                        linkToMatch={linkToMatch}
                        analytics={ANALYTICS_CLICK_ON_TITLE}
                    />
                    {showScores && (
                        <Score
                            isEnded={isEnded}
                            hasMatchHeader={hasHeader}
                            numberOfShowed={scoreCount}
                            sportId={sportId}
                            matchId={matchId}
                        />
                    )}
                </div>
                <div styleName="markets">
                    <AnalyticAttrsCtx.Provider value={ANALYTICS}>
                        <TopMarketsContainer
                            matchId={matchId}
                            hasMatchHeader={hasHeader}
                            sportId={sportId}
                            count={count}
                        />
                    </AnalyticAttrsCtx.Provider>
                </div>
            </Fragment>
        );
    };

    renderFooter = (): JSX.Element | null => {
        const {
            match,
            matchId,
            hasValidStream,
            hasValidBetradarId,
            disableMatchTracker,
        } = this.props;

        if (!match) return null;

        const { marketsCount } = match;

        const isSportEventViewable = checkIsSportEventViewable({
            hasValidStream,
            hasValidBetradarId,
            isMatchTrackerDisabled: checkIsMatchTrackerDisabled(
                disableMatchTracker,
                match.sportId
            ),
        });

        const linkToMatch = this.getLinkToMatch();

        return (
            <div styleName="footer">
                <ToggleFavoriteButton matchId={matchId} />

                <MarketsCount
                    marketsCount={marketsCount}
                    topMarketsCount={1}
                    linkToMatch={linkToMatch}
                />
                {isSportEventViewable && (
                    <div styleName="stream">
                        <StreamLink
                            linkToMatch={linkToMatch}
                            withTitle={false}
                        />
                    </div>
                )}
            </div>
        );
    };

    render() {
        debug('render', this.props);
        const { isOneMarketView } = this.state;
        const {
            hasValidStream,
            hasValidBetradarId,
            match,
            tournament,
            hasHeader,
            isResultPage,
            topMarketsCount,
            lessThanOrEqual,
            matchId,
            streamUrl,
            isStreamOpen,
            betradarId,
            route,
            disableMatchTracker,
        } = this.props;

        if (!match) return null;

        const isMatchTrackerDisabled = checkIsMatchTrackerDisabled(
            disableMatchTracker,
            match.sportId
        );

        const isSportEventViewable = checkIsSportEventViewable({
            hasValidStream,
            hasValidBetradarId,
            isMatchTrackerDisabled,
        });

        const {
            sportId,
            startTime,
            liveCoverage,
            status,
            marketsCount,
            title,
            slug,
            hasMatchLog,
            version,
        } = match;

        const isSmall = lessThanOrEqual.small;
        const isExtraSmall = lessThanOrEqual.extraSmall;
        const isNewsPage = checkIfNewsPage();

        const { isViewerOpen, isStreamAvailable } = getStreamSettings({
            hasValidStream,
            hasValidBetradarId,
            isMatchTrackerDisabled,
            isSmall,
            isStreamOpen,
        });

        const isLive = LIVE_SPORTEVENT_STATUSES.includes(status);

        const linkToMatch = this.getLinkToMatch();

        const SportEventRowComponent = (
            <AnalyticAttrsCtx.Provider value={ANALYTICS}>
                <div
                    styleName={cn('container', {
                        'is-one-market-view': isOneMarketView,
                        'is-stream-open': isViewerOpen,
                        'has-header': hasHeader,
                    })}
                >
                    <SportEventSeoMicromarking sportEventId={matchId} />
                    <SportEventInViewSubscription
                        matchId={matchId}
                        version={version}
                    />

                    {hasHeader && (
                        <div
                            styleName={cn('header', {
                                'has-valid-stream':
                                    isSportEventViewable &&
                                    (!isResultPage || isLive),
                            })}
                        >
                            {tournament && (
                                <TournamentName
                                    sportId={sportId}
                                    tournamentName={tournament.name}
                                    masterId={tournament.masterId}
                                    slug={tournament.slug}
                                    isDisabled={isNewsPage}
                                    route={route}
                                />
                            )}
                        </div>
                    )}
                    <div
                        styleName={cn('body', {
                            hasHeader,
                        })}
                    >
                        {!isExtraSmall && (
                            <ToggleFavoriteButton matchId={matchId} />
                        )}

                        <MatchDateTime
                            matchId={matchId}
                            isLive={isLive}
                            startTime={startTime}
                            liveCoverage={liveCoverage}
                            isSmall={isSmall}
                            sportId={sportId}
                        />

                        {this.renderMiddle()}

                        {!isExtraSmall && (
                            <React.Fragment>
                                <StreamMarketCount
                                    isResultPage={isResultPage}
                                    analytics={{
                                        ...ANALYTICS_PROMO,
                                        action: isViewerOpen
                                            ? Action.HidePromo
                                            : Action.WatchPromo,
                                    }}
                                    linkToMatch={linkToMatch}
                                    marketsCount={marketsCount}
                                    topMarketsCount={
                                        isOneMarketView ? 1 : topMarketsCount
                                    }
                                    streamWithTitle={!isSmall}
                                    toggleStream={
                                        isStreamAvailable
                                            ? this.toggleStream
                                            : undefined
                                    }
                                    isViewerOpen={isViewerOpen}
                                    isLive={isLive}
                                    isSportEventViewable={isSportEventViewable}
                                />
                            </React.Fragment>
                        )}
                    </div>
                    {isExtraSmall && this.renderFooter()}
                    {isViewerOpen && (
                        <div styleName="player">
                            <AnalyticAttrsCtx.Provider
                                value={ANALYTICS_ODD_BTN_STREAM}
                            >
                                <AsyncStream
                                    betradarId={betradarId}
                                    sportId={sportId}
                                    withPromoMatch={!isSmall}
                                    isHeaderAtTop={true}
                                    url={streamUrl}
                                    matchId={matchId}
                                    title={title}
                                    OddButton={AsyncOddButton}
                                    hasMatchLog={hasMatchLog}
                                />
                            </AnalyticAttrsCtx.Provider>
                        </div>
                    )}
                </div>
            </AnalyticAttrsCtx.Provider>
        );

        if (isNewsPage) {
            return (
                <SmartLink to={`${route}${App.Routes.MATCH}${slug}`}>
                    {SportEventRowComponent}
                </SmartLink>
            );
        }

        return SportEventRowComponent;
    }
}

const makeMapStateToProps = (
    state: AppState,
    ownProps: ConnectOwnProps
): MappedProps => {
    const selectMatch = makeMatchSelector({
        propsToPick: matchPropsToPick,
    });
    const selectTournamentIdByPrevMatch = makeMatchSelector({
        propsToPick: ['tournament'],
        selectMatchId: (_, props: { prevMatchId: string }) => props.prevMatchId,
    });

    const selectHasHeader = createSelector(
        selectMatch,
        selectTournamentIdByPrevMatch,
        (match, prevMatch) =>
            !(match && prevMatch && match.tournament === prevMatch.tournament)
    );

    const selectTournament = makeTournamentSelector({
        propsToPick: tournamentPropsToPick,
        selectTournamentId: selectPreviewTournamentIdFromMatch,
    });

    const selectLessThanOrEqual = makeViewportSelector('lessThanOrEqual');

    const selectIsMatchIdWithOpenedStream =
        ownProps.streamModule?.selectors.selectIsMatchIdWithOpenedStream ??
        SELECTOR_STUB;

    const sportEvent = selectMatch(state, ownProps);
    const tournament = selectTournament(state, ownProps);
    const lessThanOrEqual = selectLessThanOrEqual(state, ownProps);
    const streamUrl = selectMobileMatchStream(state, ownProps);
    const competitorsValid = selectMatchCompetitorsValid(state, ownProps);
    const isStreamOpen = selectIsMatchIdWithOpenedStream(state, ownProps);

    let hasHeader = selectHasHeader(state, ownProps);

    const markets = selectTopMarketsByMatchId(state, {
        matchId: ownProps.matchId,
    });

    const isGreat = lessThanOrEqual.great;
    const isSmall = lessThanOrEqual.small;
    const isExtraSmall = lessThanOrEqual.extraSmall;

    const { hasValidStream, hasValidBetradarId } = selectIsSportEventViewable(
        state,
        {
            matchId: ownProps.matchId,
        }
    );

    const count = isSmall ? 2 : 3;
    const scoreCount = isGreat ? (isSmall ? 1 : 2) : 3;

    const isExistHcpOrTotal = getIsExistHcpOrTotal(markets);

    const isOneMarketView =
        !!sportEvent &&
        (getTopMarketComponentsBySport(sportEvent.sportId, { count }).length ===
            1 ||
            !isExistHcpOrTotal) &&
        !isExtraSmall;

    if (!hasHeader) {
        const prevMarkets = selectTopMarketsByMatchId(state, {
            matchId: ownProps.prevMatchId,
        });

        const prevIsExistHcpOrTotal = getIsExistHcpOrTotal(prevMarkets);

        const marketsNames = markets.map(m => m.name);
        const prevMarketsNames = prevMarkets.map(m => m.name);

        hasHeader =
            difference(marketsNames, prevMarketsNames).length > 0 ||
            prevIsExistHcpOrTotal !== isExistHcpOrTotal;
    }

    const betradarId = selectBetradarId(state, {
        matchId: ownProps.matchId,
    });
    const route = selectRouteBySportEvent(state, {
        matchId: ownProps.matchId,
    });

    return {
        hasHeader,
        match: sportEvent,
        tournament,
        lessThanOrEqual,
        hasValidStream,
        hasValidBetradarId,
        isStreamOpen,
        competitorsValid,
        streamUrl,
        topMarketsCount: markets.length,
        markets,
        isOneMarketView,
        count,
        scoreCount,
        betradarId,
        route,
    };
};

const mapDispatchToProps = (
    dispatch: DispatchProp['dispatch'],
    { streamModule }: ConnectOwnProps
): MappedActions => {
    if (!streamModule)
        return {
            addMatchId: noop,
            deleteMatchId: noop,
        };

    const {
        actions: { addMatchId, deleteMatchId },
    } = streamModule;

    return {
        addMatchId: (payload: Arg1<typeof addMatchId>) =>
            dispatch(addMatchId(payload)),
        deleteMatchId: (payload: Arg1<typeof deleteMatchId>) =>
            dispatch(deleteMatchId(payload)),
    };
};

export default compose<React.ComponentClass<OwnProps>>(
    // withResponseContainer,
    withStreamModule,
    withStreamWidgetSettings,
    connect<MappedProps, MappedActions, ConnectOwnProps>(
        makeMapStateToProps,
        mapDispatchToProps
    ),
    makePure({ maxDepth: 3 })
)(SportEventRow);

const getIsExistHcpOrTotal = (markets: View.Market[]): boolean =>
    markets.some(m => isHCP(m) || isTotal(m));
